import { Add, Delete } from '@mui/icons-material';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import { DateTime } from 'luxon';
import qs from 'qs';
import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccordionSection from '../../../components/AccordionSection';
import DrawerLink from '../../../components/common/drawers/ModuleDrawer/DrawerLink';
import TemplateModal from '../../../components/common/TemplateModal/TemplateModal';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllEventTemplate } from '../../../services/react-query/event_templates';
import { fetchEvent } from '../../../services/react-query/events';
import { Event } from '../../../types/Event';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import StatusPill from '../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes, parseIsoWithDefault } from '../../../utility/dateUtils';

interface ISubeventsSectionProps {
  events: Event[];
  setEvents: (events: Event[]) => void;
}

export default function SubeventsSection({ events, setEvents }: ISubeventsSectionProps) {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const { disabled } = useContext(DisableContext);
  const { data: eventTemplateData } = useGetAllEventTemplate({
    params: { per_page: 0 },
  });
  const eventTemplates = eventTemplateData?.data || [];

  const isNew = uuid === 'new';
  const orderedEvents = _.sortBy(events, 'start_at');

  const updateEvent = (uuid: string, partial: Partial<Event>) => {
    setEvents(_.map(events, (event) => (event.uuid === uuid ? { ...event, ...partial } : event)));
  };

  const updateDates = (uuid: string, start_at: DateTime<true>) =>
    updateEvent(uuid, {
      start_at: start_at.toISO(),
      end_at: start_at.plus({ days: 1 }).toISO(),
    });

  const handleDelete = () => {
    if (!deleteUuid) return;
    setEvents(_.reject(events, { uuid: deleteUuid }));
    setDeleteUuid(null);
  };

  const handleAdd = async (templateUuid: string | null) => {
    if (isNew) {
      const newEvent = await fetchEvent('new', templateUuid ?? undefined);

      setEvents([...events, newEvent]);
      setAddModalOpen(false);
    } else {
      const urlParams = qs.stringify({ template: templateUuid, parent: uuid });
      navigate(`/${ROUTE_PATHS.EVENTS}/new?${urlParams}`);
    }
  };

  const eventRow = (event: Event) => (
    <TableRow>
      <TableCell>
        {isNew ? (
          <TextField value={event.name} onChange={(e) => updateEvent(event.uuid, { name: e.target.value })} />
        ) : (
          <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid}>
            {event.display_name}
          </DrawerLink>
        )}
      </TableCell>
      <TableCell>
        {isNew ? (
          <DatePicker onChange={(value) => value?.isValid && updateDates(event.uuid, value)} value={parseIsoWithDefault(event.start_at)} />
        ) : (
          humanizeEventDateTimes(event)
        )}
      </TableCell>
      <TableCell>
        <StatusPill status={event.status} />
      </TableCell>
      {isNew && (
        <TableCell>
          <IconButton onClick={() => setDeleteUuid(event.uuid)} size="small">
            <Delete />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );

  const addButton = (
    <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setAddModalOpen(true)} disabled={disabled}>
      Add
    </ResponsiveButton>
  );

  return (
    <>
      <AccordionSection
        title="Subevents"
        headerElements={addButton}
        collapseId={UserSettingsCollapseKeys.EVENT_ITEM_SUBEVENTS}
        hasData={events.length > 0}
        scrollable
      >
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                {isNew && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>{_.map(orderedEvents, eventRow)}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>
      <ConfirmationModal
        isOpen={!!deleteUuid}
        text={'Are you sure you want to remove this event?'}
        onCancel={() => setDeleteUuid(null)}
        onConfirm={handleDelete}
      />
      <TemplateModal
        isOpen={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onCreate={handleAdd}
        templates={eventTemplates}
        title="Please choose which event template to use"
      />
    </>
  );
}
